// @flow
import React, {useEffect, useMemo, useRef} from 'react';
import {Animated, ScrollView, StyleSheet, View} from 'react-native';
import {Text} from 'trpc-react-native-ui';
import config from '../../../config';
import I18n from '../../../lib/i18n';
import {getFlag} from '../../../../resources/country-flags';
import theme from '../../../theme-config';

type AnimatedFlagProps = {
  alpha3: string,
  isActive: boolean,
};

const AnimatedFlag = ({alpha3, isActive}: AnimatedFlagProps) => {
  const opacity = useRef(new Animated.Value(isActive ? 1 : 0.3)).current;
  const scale = useRef(new Animated.Value(isActive ? 1 : 0.85)).current;
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: isActive ? 1 : 0.3,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(scale, {
        toValue: isActive ? 1 : 0.85,
        friction: 8,
        tension: 40,
        useNativeDriver: true,
      }),
    ]).start();
  }, [isActive]);

  return (
    <Animated.Image
      source={getFlag(alpha3)}
      style={[styles.flag, {opacity, transform: [{scale}]}]}
      resizeMode="cover"
    />
  );
};

type Props = {
  type: string,
  selectedOption?: Object,
};

const RoamingTerritories = ({type, selectedOption}: Props) => {
  const mapping = config.roamingTerritoryMapping || {};

  // Compute superset of all territories for this slider type
  const allTerritories = useMemo(() => {
    const prefix = type === 'DATA' ? 'KAIZEN_OPTDATA_ROA' : 'KAIZEN_OPT_IDD';
    const merged = [];
    Object.keys(mapping).forEach(key => {
      if (key.startsWith(prefix)) {
        mapping[key].forEach(code => {
          if (!merged.includes(code)) merged.push(code);
        });
      }
    });
    return merged;
  }, [type, mapping]);

  // Active territories based on selected option
  const activeTerritories = useMemo(() => {
    if (!selectedOption?.ouid || !selectedOption?.name) return [];
    return mapping[selectedOption.name] || [];
  }, [selectedOption, mapping]);

  if (allTerritories.length === 0) return null;

  const hasActive = activeTerritories.length > 0;

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.flagsRow}
        contentContainerStyle={styles.flagsRowContent}
      >
        {allTerritories.map((alpha3) => (
          <AnimatedFlag
            key={alpha3}
            alpha3={alpha3}
            isActive={activeTerritories.includes(alpha3)}
          />
        ))}
      </ScrollView>
      {hasActive ? (
        <Text style={styles.text}>
          {I18n.t('stepper.roamingTerritoriesPrefix')}{' '}
          {activeTerritories
            .map(code => I18n.t('countries.' + code.toUpperCase()))
            .join(', ')}
        </Text>
      ) : (
        <Text style={styles.hintText}>
          {type === 'DATA'
            ? I18n.t('stepper.roamingDataZeroHint')
            : I18n.t('stepper.roamingVoiceZeroHint')}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    marginBottom: 10,
    alignItems: 'center',
  },
  flagsRow: {
    height: 24,
    flexGrow: 0,
    flexShrink: 0,
  },
  flagsRowContent: {
    alignItems: 'center',
  },
  flag: {
    width: 35,
    height: 20,
    marginRight: 6,
    borderRadius: 2,
    overflow: 'hidden',
  },
  text: {
    fontSize: 12,
    color: theme.gray.normalLight,
    marginTop: 6,
    height: 40,
  },
  hintText: {
    fontSize: 12,
    color: theme.gray.normalLight,
    marginTop: 6,
    fontStyle: 'italic',
    height: 30,
  },
});

export default RoamingTerritories;
